import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, DatePicker, Drawer, Empty, Form, Input, InputNumber, Modal, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import { formatDisplayDate, isExpiredDate, parseFlexibleDate } from './panels/expiryUtils';

const { Title, Text } = Typography;

const buildVehicleLabel = (record) => {
  return [record.make, record.model, record.registrationNumber].filter(Boolean).join(' ') || 'Unnamed vehicle/equipment';
};

const getRegoDateValue = (record) => {
  return record?.expiryDate || record?.registrationExpiryDate || record?.nextServiceDue || '';
};

export default function CompanyVehicleRegoPage() {
  const authContext = useAuth();
  const companyId = authContext.additionalUserInfo?.companyId || authContext.additionalUserInfo?.company?.id || authContext.additionalUserInfo?.company?.companyId;
  const token = authContext.token;
  const role = authContext.role || authContext.additionalUserInfo?.role;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const expiredRecords = useMemo(
    () => records.filter((record) => isExpiredDate(getRegoDateValue(record))),
    [records]
  );

  useEffect(() => {
    fetchRecords();
  }, [companyId, token]);

  const fetchRecords = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/carrego/company/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load company rego records');
      }

      const data = await response.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Failed to load vehicle and equipment rego records.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      vin: record.vin || '',
      registrationNumber: record.registrationNumber || '',
      registrationState: record.registrationState || '',
      make: record.make || '',
      model: record.model || '',
      yearOfManufacture: record.yearOfManufacture ?? null,
      bodyType: record.bodyType || '',
      colour: record.colour || '',
      servicingKms: record.servicingKms ?? null,
      servicingDate: parseFlexibleDate(record.servicingDate),
      nextServiceDue: parseFlexibleDate(getRegoDateValue(record)),
      servicedBy: record.servicedBy || '',
      totalCost: record.totalCost ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      const payload = {
        ...(editingRecord || {}),
        vin: values.vin || '',
        registrationNumber: values.registrationNumber,
        registrationState: values.registrationState || '',
        make: values.make || '',
        model: values.model || '',
        yearOfManufacture: values.yearOfManufacture ?? null,
        bodyType: values.bodyType || '',
        colour: values.colour || '',
        servicingKms: values.servicingKms ?? null,
        servicingDate: values.servicingDate ? values.servicingDate.format('YYYY-MM-DD') : '',
        nextServiceDue: values.nextServiceDue ? values.nextServiceDue.format('YYYY-MM-DD') : '',
        servicedBy: values.servicedBy || '',
        totalCost: values.totalCost ?? null,
        servicingHistory: editingRecord?.servicingHistory || [],
      };

      await axios.post(`${baseUrl_SPB}/api/v1/carrego/company/${companyId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      await fetchRecords();
    } catch (err) {
      if (err?.errorFields) return;
      console.error(err);
      setError('Failed to save vehicle or equipment rego record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete rego record',
      content: `Delete ${buildVehicleLabel(record)}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl_SPB}/api/v1/carrego/company/${companyId}/${record.id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          await fetchRecords();
        } catch (err) {
          console.error(err);
          setError('Failed to delete vehicle or equipment rego record.');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Registration',
      key: 'registration',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.registrationNumber || '—'}</Text>
          <Text type="secondary">{record.registrationState || '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Vehicle / Equipment',
      key: 'vehicle',
      render: (_, record) => [record.make, record.model, record.bodyType].filter(Boolean).join(' / ') || '—',
    },
    {
      title: 'VIN',
      dataIndex: 'vin',
      key: 'vin',
      render: (value) => value || '—',
    },
    {
      title: 'Expiry / Due Date',
      key: 'nextServiceDue',
      render: (_, record) => (
        <Space size={6}>
          <span>{formatDisplayDate(getRegoDateValue(record))}</span>
          {isExpiredDate(getRegoDateValue(record)) ? <Tag color="red">Expired</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Service Date',
      dataIndex: 'servicingDate',
      key: 'servicingDate',
      render: (value) => formatDisplayDate(value),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      render: (_, record) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Space>
      ),
    },
  ];

  if (!['ROLE_CMANAGER', 'ROLE_SADMIN'].includes(role)) {
    return <Alert type="error" showIcon message="Access denied" description="Only company managers can manage rego records." />;
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Vehicle / Equipment Rego</Title>
          <Text type="secondary">Track company vehicle and equipment rego details, plus due dates.</Text>
        </div>

        {error ? <Alert type="error" showIcon message={error} closable onClose={() => setError('')} /> : null}
        {expiredRecords.length ? (
          <Alert
            type="warning"
            showIcon
            message={`Expired rego or due dates found: ${expiredRecords.map((record) => buildVehicleLabel(record)).join(', ')}`}
            closable
          />
        ) : null}

        <Card
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add rego
            </Button>
          }
        >
          {records.length === 0 ? (
            <Empty description="No vehicle or equipment rego records" />
          ) : (
            <Table
              rowKey={(record) => record.id || record.registrationNumber || record.vin}
              dataSource={records}
              columns={columns}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 900 }}
            />
          )}
        </Card>
      </Space>

      <Drawer
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        title={editingRecord ? 'Edit Vehicle / Equipment Rego' : 'Add Vehicle / Equipment Rego'}
        width={520}
        extra={
          <Space>
            <Button onClick={() => {
              setModalOpen(false);
              setEditingRecord(null);
              form.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              {editingRecord ? 'Update' : 'Save'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="registrationNumber" label="Registration Number" rules={[{ required: true, message: 'Registration number is required' }]}>
            <Input placeholder="e.g. ABC123" />
          </Form.Item>
          <Form.Item name="registrationState" label="Registration State">
            <Input placeholder="e.g. NSW" />
          </Form.Item>
          <Form.Item name="vin" label="VIN">
            <Input placeholder="Vehicle identification number" />
          </Form.Item>
          <Form.Item name="make" label="Make">
            <Input placeholder="e.g. Toyota" />
          </Form.Item>
          <Form.Item name="model" label="Model">
            <Input placeholder="e.g. Hilux" />
          </Form.Item>
          <Form.Item name="bodyType" label="Vehicle / Equipment Type">
            <Input placeholder="e.g. Ute, Van, Trailer, Excavator" />
          </Form.Item>
          <Form.Item name="colour" label="Colour">
            <Input placeholder="e.g. White" />
          </Form.Item>
          <Form.Item name="yearOfManufacture" label="Year of Manufacture">
            <InputNumber style={{ width: '100%' }} min={1900} max={2500} />
          </Form.Item>
          <Form.Item name="nextServiceDue" label="Rego Expiry / Due Date" rules={[{ required: true, message: 'A due date is required' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="servicingDate" label="Servicing Date">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="servicingKms" label="Servicing KMs">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="servicedBy" label="Serviced By">
            <Input placeholder="Workshop or supplier" />
          </Form.Item>
          <Form.Item name="totalCost" label="Total Cost">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
